<script>
    import { createEventDispatcher } from 'svelte';
    import { backendService } from '../../../services/backendService.js';
    import { claimsStore } from '../../../stores/editorsStore';
    import AccessibleModal from '../../shared/AccessibleModal.svelte';

    export let show = false;
    
    const dispatch = createEventDispatcher();

    // Local state
    let limitations = [];
    let newLimitation = '';
    let claims = {};
    let selectedClaims = {}; // Track which claims are selected for assignment
    let loading = false;
    let error = null;
    let assigned = false;
    let claimToLimitationMap = null;

    // Parse claims from the claims editor content
    function parseClaimsFromText(claimsText) {
        const claimMap = {};
        const claimRegex = /(\d+)\.\s*(.*?)(?=\s*\d+\.\s*|$)/gs;
        let match;

        while ((match = claimRegex.exec(claimsText)) !== null) {
            const claimNumber = match[1];
            const claimText = match[2].trim();
            claimMap[claimNumber] = claimText;
        }

        return claimMap;
    }
    
    // Determines if a claim is an independent claim
    // (For internal purposes, we're checking for claims that start with "A" or "An")
    function isPseudoIndependentClaim(claimText) {
        // Trim and check if claim starts with "A" or "An" followed by a space
        return /^A\s+/i.test(claimText) || /^An\s+/i.test(claimText);
    }

    // Initialize claims when the modal opens
    $: if (show) {
        if ($claimsStore.content && $claimsStore.content.trim()) {
            claims = parseClaimsFromText($claimsStore.content);
            
            // Initially select all claims (including independent claims)
            selectedClaims = Object.fromEntries(
                Object.entries(claims).map(([claimNum, claimText]) => {
                    return [claimNum, true]; // Select all claims by default
                })
            );
        } else {
            claims = {};
            selectedClaims = {};
        }
    }

    // Add a new limitation to the list
    function addLimitation() {
        if (newLimitation.trim()) {
            limitations = [...limitations, newLimitation.trim()];
            newLimitation = '';
        }
    }
    
    // Extract limitations from claim 1 automatically
    function extractFromClaim1() {
        const claim1Text = claims['1'];
        if (!claim1Text) {
            error = "No claim 1 found to extract limitations from";
            return;
        }
        
        // Find the first colon position
        const colonPos = claim1Text.indexOf(':');
        
        let textToSplit;
        if (colonPos !== -1) {
            // If colon exists, extract text after it
            textToSplit = claim1Text.substring(colonPos + 1);
            console.log(`Found colon at position ${colonPos}, extracting limitations from text after colon`);
        } else {
            // If no colon, use the entire claim text
            textToSplit = claim1Text;
            console.log('No colon found, extracting limitations from entire claim 1');
        }
        
        // Split on semicolons to get limitations
        const extractedLimitations = textToSplit
            .split(';')
            .map(lim => lim.trim())
            .filter(lim => lim.length > 0)
            .map(lim => {
                // Remove trailing period if present
                if (lim.endsWith('.')) {
                    lim = lim.slice(0, -1);
                }
                return lim;
            });
        
        if (extractedLimitations.length > 0) {
            // Replace current limitations with extracted ones
            limitations = extractedLimitations;
            error = null;
            console.log(`Extracted ${limitations.length} limitations from claim 1:`, limitations);
        } else {
            error = "Could not extract limitations from claim 1. Please add them manually.";
        }
    }

    // Remove a limitation from the list
    function removeLimitation(index) {
        limitations = limitations.filter((_, i) => i !== index);
    }

    // Handle keydown events in the new limitation input
    function handleKeydown(e) {
        if (e.key === 'Enter') {
            e.preventDefault();
            addLimitation();
        }
    }

    // Toggle claim selection
    function toggleClaimSelection(claimNum, checked) {
        // Use the provided checkbox state instead of toggling
        selectedClaims[claimNum] = checked;
        selectedClaims = {...selectedClaims}; // Trigger reactivity

        // Reset assignment if we change selections
        if (assigned) {
            assigned = false;
            claimToLimitationMap = null;
        }

        console.log('Claim selection changed:', claimNum, 'is now', checked);
        console.log('Current selections:', selectedClaims);
    }

    // Close the modal
    function handleClose() {
        if (loading) return;
        
        // Clear the progress interval if it's running
        if (applyProgressInterval) {
            clearInterval(applyProgressInterval);
            applyProgressInterval = null;
        }
        
        // Reset progress state
        applyingToSpec = false;
        rawApplyProgress = 0;
        startTime = null;
        estimatedTotalTime = null;
        timeRemaining = null;
        
        show = false;
        dispatch('close');
    }

    // Assign the claims
    async function assignClaims() {
        if (limitations.length === 0) {
            error = "Please add at least one limitation";
            return;
        }

        if (Object.keys(claims).length === 0) {
            error = "No claims found. Please add claims in the claims editor.";
            return;
        }

        // Check if any claims are selected
        const selectedClaimsList = Object.entries(selectedClaims)
            .filter(([_, isSelected]) => isSelected)
            .map(([claimNum, _]) => claimNum);

        if (selectedClaimsList.length === 0) {
            error = "Please select at least one claim for assignment";
            return;
        }

        loading = true;
        error = null;
        assigned = false;

        try {
            // Filter claims to only use the selected ones
            const selectedClaimsMap = {};
            selectedClaimsList.forEach(claimNum => {
                selectedClaimsMap[claimNum] = claims[claimNum];
            });

            // Get the mapping of claims to their best limitations
            // This may also include detailed_description in debug mode
            const response = await backendService.assignClaimsToLimitations(limitations, selectedClaimsMap);
            
            console.log('ClaimAssignmentModal: Got response from backend:', response);
            
            // Extract the claim_to_element_map from the response
            claimToLimitationMap = response.claim_to_element_map;
            
            // Keep the detailed_description for passing to the parent component
            if (response.detailed_description) {
                claimToLimitationMap.detailed_description = response.detailed_description;
            }
            
            assigned = true;
        } catch (err) {
            console.error("Error assigning claims to limitations:", err);
            error = err.message || "Failed to assign claims to limitations";
        } finally {
            loading = false;
        }
    }

    // Format a score as a percentage
    function formatScore(score) {
        return Math.round(score * 100) + '%';
    }
    
    // Group claims by limitation for display
    $: groupedResults = (() => {
        if (!assigned || !claimToLimitationMap) return { assigned: [], unassigned: [] };
        
        // Initialize all limitations first
        const limitationGroups = {};
        limitations.forEach((limitation, idx) => {
            limitationGroups[idx] = {
                limitation_text: limitation,
                limitation_idx: idx,
                claims: []
            };
        });
        
        // Track unassigned claims (0% confidence)
        const unassignedClaims = [];
        
        // Process the claim-to-limitation mapping
        Object.entries(claimToLimitationMap).forEach(([claimNum, claimData]) => {
            // Skip the detailed_description property if it exists
            if (claimNum === 'detailed_description') return;
            
            const topLimitation = claimData?.top_element;
            if (!topLimitation) return;
            
            const limitationIdx = topLimitation.element_idx;
            const score = topLimitation.score || 0;
            
            const claimInfo = {
                claim_num: claimNum,
                claim_text: claimData.claim_text,
                score: score
            };
            
            // If score is 0, add to unassigned
            if (score === 0) {
                unassignedClaims.push(claimInfo);
            } else {
                // Add claim to the appropriate limitation group
                if (limitationGroups[limitationIdx]) {
                    limitationGroups[limitationIdx].claims.push(claimInfo);
                }
            }
        });
        
        // Sort limitation groups by limitation index, include all limitations
        const assignedGroups = Object.values(limitationGroups)
            .sort((a, b) => a.limitation_idx - b.limitation_idx);
        
        return {
            assigned: assignedGroups,
            unassigned: unassignedClaims.sort((a, b) => parseInt(a.claim_num) - parseInt(b.claim_num))
        };
    })();

    // State for applying progress
    let applyingToSpec = false;
    let rawApplyProgress = 0;
    let applyProgressInterval;
    let startTime = null;
    let estimatedTotalTime = null;
    let timeRemaining = null;
    let claimGroups = []; // Store claim groups for paragraphing
    
    // Make applyProgress and progress bar width reactive
    $: applyProgress = rawApplyProgress;
    $: progressBarWidth = `${Math.max(5, Math.round(applyProgress))}%`;
    
    // Format time based on duration - seconds for < 1 minute, minutes otherwise
    function formatTime(seconds, forceMinutes = false) {
        if (!forceMinutes && estimatedTotalTime && estimatedTotalTime < 60) {
            // If total time is less than a minute, show in seconds
            return `${Math.round(seconds)}s`;
        } else {
            // Otherwise show in minutes with 2 decimal places
            const minutes = seconds / 60;
            return `${minutes.toFixed(2)} minutes`;
        }
    }

    // Apply the assignment to the spec
    async function applyToSpec() {
        if (!claimToLimitationMap) return;

        // Double-check that we have claims and limitations
        if (limitations.length === 0 || Object.keys(claims).length === 0) {
            error = "Cannot apply: missing limitations or claims";
            return;
        }

        // Start progress animation
        applyingToSpec = true;
        rawApplyProgress = 0;
        error = null;
        startTime = Date.now();
        estimatedTotalTime = null;
        timeRemaining = null;
        
        // Check if we're in debug mode (has detailed_description from backend)
        const isDebugMode = !!claimToLimitationMap.detailed_description;
        
        console.log("Starting literal support generation");
        
        try {
            // Extract only the assigned claims for literal support generation
            const assignedClaims = {};
            Object.entries(claimToLimitationMap).forEach(([claimNum, claimData]) => {
                if (claimNum !== 'detailed_description' && claimData.claim_text) {
                    assignedClaims[claimNum] = claimData.claim_text;
                }
            });
            
            console.log(`Generating literal support for ${Object.keys(assignedClaims).length} claims`);
            
            // Generate literal support for all assigned claims
            // Use 'detailed_description' context to keep claims separate
            const literalSupportResult = await backendService.generateLiteralSupportBatch(assignedClaims, {
                context: 'detailed_description',  // Keep claims separate for detailed description
                onProgress: (progress) => {
                    if (progress.type === 'init') {
                        console.log(`Starting literal support generation for ${progress.totalClaims} claims in ${progress.totalGroups} groups`);
                        claimGroups = progress.claimGroups || [];
                        console.log('Received claim groups:', claimGroups);
                    } else if (progress.type === 'group') {
                        // Update progress based on group processing
                        rawApplyProgress = Math.min(95, progress.progress * 90); // Cap at 95% until fully complete
                        console.log(`Generated literal support for group ${progress.groupIndex + 1} (claims ${progress.claimNumbers.join(', ')}) - ${progress.processedGroups}/${progress.totalGroups}`);
                        
                        if (progress.errors && progress.errors.length > 0) {
                            console.warn('Errors in group processing:', progress.errors);
                        }
                        
                        // Calculate time estimates based on group processing
                        if (progress.processedGroups > 0 && startTime) {
                            const elapsed = (Date.now() - startTime) / 1000; // in seconds
                            const avgTimePerGroup = elapsed / progress.processedGroups;
                            estimatedTotalTime = avgTimePerGroup * progress.totalGroups;
                            timeRemaining = avgTimePerGroup * (progress.totalGroups - progress.processedGroups);
                        }
                    } else if (progress.type === 'group_error') {
                        console.error(`Error processing group ${progress.groupIndex + 1}:`, progress.message);
                        // Still update progress since we processed (even if with errors)
                        rawApplyProgress = Math.min(95, (progress.processedGroups / progress.totalGroups) * 90);
                    } else if (progress.type === 'complete') {
                        console.log('Literal support generation complete');
                        timeRemaining = 0;
                    }
                }
            });
            
            // Check if we got literal support for all claims
            if (!literalSupportResult.isComplete) {
                throw new Error('Literal support generation did not complete successfully');
            }
            
            // Animate to 100%
            rawApplyProgress = 100;
            
            // Dispatch the apply event with literal support data
            console.log("Literal support generated: Applying to spec");
            dispatch('apply', { 
                claimToLimitationMap, 
                limitations, 
                claims,
                literalSupport: literalSupportResult.literalSupport,
                paragraphs: literalSupportResult.paragraphs, // New grouped paragraphs
                claimGroups: literalSupportResult.claimGroups 
            });
            
            // Clean up after a short delay
            setTimeout(() => {
                applyingToSpec = false;
                rawApplyProgress = 0;
                startTime = null;
                estimatedTotalTime = null;
                timeRemaining = null;
                handleClose();
            }, 300);
            
        } catch (err) {
            console.error("Error generating literal support:", err);
            error = "Failed to generate literal support. Using original claims instead.";
            
            // Fallback: dispatch without literal support
            setTimeout(() => {
                dispatch('apply', { claimToLimitationMap, limitations, claims });
                applyingToSpec = false;
                rawApplyProgress = 0;
                startTime = null;
                estimatedTotalTime = null;
                timeRemaining = null;
                handleClose();
            }, 2000); // Show error for 2 seconds
        }
    }
</script>

<AccessibleModal 
    bind:show={show}
    variant="spec"
    title="Assign Claims for Specification"
    titleId="claim-assignment-modal-title"
    on:close={handleClose}
    closeOnBackdropClick={!loading}
    data-testid="claim-assignment-modal"
>
    <div class="modal-content">
            <div class="instructions">
                <p>Add limitations from your invention in the top panel. Then click "Assign" to match claims to limitations.</p>
                <p>Your assigned content will be added to the Detailed Description section.</p>
            </div>
            
            <div class="assignment-container">
                <!-- Limitations Panel -->
                <div class="panel limitations-panel">
                    <h3>Limitations</h3>
                    <div class="limitations-input">
                        <input 
                            type="text" 
                            bind:value={newLimitation}
                            placeholder="Enter limitation description..." 
                            on:keydown={handleKeydown}
                            disabled={loading}
                            aria-label="Enter limitation description"
                        />
                        <button 
                            class="modal-button modal-button-accent" 
                            on:click={addLimitation}
                            disabled={!newLimitation.trim() || loading}
                            aria-label="Add limitation"
                        >
                            Add
                        </button>
                        <button 
                            class="modal-button modal-button-secondary" 
                            on:click={extractFromClaim1}
                            disabled={!claims['1'] || loading}
                            aria-label="Extract limitations from claim 1"
                            title="Automatically extract limitations from claim 1"
                        >
                            <i class="fas fa-magic"></i> Extract from Claim 1
                        </button>
                    </div>
                    
                    <div class="limitations-list">
                        {#if limitations.length === 0}
                            <div class="empty-message">No limitations added yet</div>
                        {:else}
                            {#each limitations as limitation, index}
                                <div class="limitation-item">
                                    <div class="limitation-number">{index + 1}</div>
                                    <div class="limitation-text">{limitation}</div>
                                    <button 
                                        class="modal-button modal-button-danger" 
                                        on:click={() => removeLimitation(index)}
                                        disabled={loading}
                                        aria-label={`Remove limitation ${index + 1}`}
                                    >
                                        ×
                                    </button>
                                </div>
                            {/each}
                        {/if}
                    </div>
                </div>
                
                <!-- Claims Panel -->
                <div class="panel claims-panel">
                    <h3>Claims</h3>
                    <div class="claims-info">
                        Check/uncheck the boxes to include/exclude claims from assignment.
                        <div class="claim-selection-controls">
                            <button
                                class="modal-button modal-button-accent"
                                on:click={() => {
                                    Object.keys(claims).forEach(claimNum => selectedClaims[claimNum] = true);
                                    selectedClaims = {...selectedClaims};
                                    if (assigned) {
                                        assigned = false;
                                    }
                                }}
                                disabled={loading}
                                title="Select all claims"
                            >
                                Select All
                            </button>
                            <button
                                class="modal-button modal-button-secondary"
                                on:click={() => {
                                    // Select only non-pseudo-independent claims
                                    Object.entries(claims).forEach(([claimNum, claimText]) => {
                                        selectedClaims[claimNum] = !isPseudoIndependentClaim(claimText);
                                    });
                                    selectedClaims = {...selectedClaims};
                                    if (assigned) {
                                        assigned = false;
                                    }
                                }}
                                disabled={loading}
                                title="Select dependent claims only"
                            >
                                Select Dependents
                            </button>
                            <button
                                class="modal-button modal-button-secondary"
                                on:click={() => {
                                    Object.keys(claims).forEach(claimNum => selectedClaims[claimNum] = false);
                                    selectedClaims = {...selectedClaims};
                                    if (assigned) {
                                        assigned = false;
                                    }
                                }}
                                disabled={loading}
                                title="Deselect all claims"
                            >
                                Deselect All
                            </button>
                        </div>
                    </div>
                    <div class="claims-list">
                        {#if Object.keys(claims).length === 0}
                            <div class="empty-message">No claims found. Please add claims in the Claims editor.</div>
                        {:else}
                            {#each Object.entries(claims) as [claimNum, claimText]}
                                {@const isPseudo = isPseudoIndependentClaim(claimText)}
                                <div class="claim-item" class:deselected={!selectedClaims[claimNum]} class:independent-claim={isPseudo}>
                                    <div class="claim-checkbox">
                                        <input
                                            type="checkbox"
                                            id="claim-{claimNum}"
                                            checked={selectedClaims[claimNum]}
                                            on:change={(e) => toggleClaimSelection(claimNum, e.target.checked)}
                                            disabled={loading}
                                            aria-label={`Select claim ${claimNum}`}
                                        />
                                    </div>
                                    <div class="claim-number" title={isPseudo ? "Independent claim" : "Dependent claim"}>{claimNum}</div>
                                    <div class="claim-text whitespace-preserve">
                                        {claimText}
                                    </div>
                                </div>
                            {/each}
                        {/if}
                    </div>
                </div>
            </div>
            
            <div class="action-bar">
                <button
                    class="modal-button modal-button-accent"
                    on:click={assignClaims}
                    disabled={limitations.length === 0 || Object.keys(claims).length === 0 || loading}
                >
                    {loading ? 'Assigning...' : 'Assign'}
                </button>
                
                {#if error}
                    <div class="error-message" role="alert">{error}</div>
                {/if}
            </div>
            
            {#if assigned && claimToLimitationMap}
                <div class="results-container">
                    <h3>Assignment Results</h3>

                    <div class="results-list">
                        <!-- Assigned Claims Section -->
                        {#if groupedResults.assigned && groupedResults.assigned.length > 0}
                            {#each groupedResults.assigned as group}
                                <div class="limitation-group">
                                    <div class="limitation-header">
                                        <span class="limitation-label">Limitation {group.limitation_idx + 1}: {group.limitation_text}</span>
                                    </div>
                                    
                                    <!-- Sort claims by number within each limitation group -->
                                    {#each group.claims.sort((a, b) => parseInt(a.claim_num) - parseInt(b.claim_num)) as claim}
                                        <div class="result-item">
                                            <div class="result-claim">
                                                <span class="claim-label">Claim {claim.claim_num}:</span>
                                                <span class="claim-content whitespace-preserve">{claim.claim_text}</span>
                                                <span class="match-score">{formatScore(claim.score)}</span>
                                            </div>
                                        </div>
                                    {/each}
                                </div>
                            {/each}
                        {/if}
                        
                        <!-- Unassigned Claims Section -->
                        {#if groupedResults.unassigned && groupedResults.unassigned.length > 0}
                            <div class="limitation-group unassigned-group">
                                <div class="limitation-header">
                                    <span class="limitation-label">Unassigned Claims (0% confidence)</span>
                                </div>
                                
                                {#each groupedResults.unassigned as claim}
                                    <div class="result-item">
                                        <div class="result-claim">
                                            <span class="claim-label">Claim {claim.claim_num}:</span>
                                            <span class="claim-content whitespace-preserve">{claim.claim_text}</span>
                                            <span class="match-score unassigned-score">{formatScore(claim.score)}</span>
                                        </div>
                                    </div>
                                {/each}
                            </div>
                        {/if}
                        
                        <!-- No Results Message -->
                        {#if (!groupedResults.assigned || groupedResults.assigned.length === 0) && (!groupedResults.unassigned || groupedResults.unassigned.length === 0)}
                            <div class="no-results-message">No assignment results to display</div>
                        {/if}
                    </div>

                    <div class="apply-action">
                        <button class="modal-button modal-button-accent" on:click={applyToSpec} disabled={applyingToSpec}>
                            {#if applyingToSpec}
                                Applying to Detailed Description...
                            {:else}
                                Apply to Detailed Description
                            {/if}
                        </button>
                        
                        {#if applyingToSpec}
                            <div class="progress-wrapper" aria-live="polite">
                                <div class="progress-container" role="progressbar" aria-valuenow={Math.round(applyProgress)} aria-valuemin="0" aria-valuemax="100">
                                    <div 
                                        class="progress-fill"
                                        style="transform: scaleX({Math.round(applyProgress)/100}); transform-origin: left center;"
                                    ></div>
                                </div>
                                <div class="progress-text">
                                    <div>{Math.round(applyProgress)}%</div>
                                    {#if estimatedTotalTime && timeRemaining !== null}
                                        <div class="time-estimate">
                                            Est. total: {formatTime(estimatedTotalTime)} · Remaining: {formatTime(timeRemaining)}
                                        </div>
                                    {/if}
                                </div>
                            </div>
                        {/if}
                    </div>
                </div>
            {/if}
    </div>
</AccessibleModal>

<style>
    /* Custom scrollbar styling for all scrollable components */
    .claims-list::-webkit-scrollbar,
    .limitations-list::-webkit-scrollbar,
    .results-list::-webkit-scrollbar {
        width: 8px;
    }
    
    .claims-list::-webkit-scrollbar-track,
    .limitations-list::-webkit-scrollbar-track,
    .results-list::-webkit-scrollbar-track {
        background: var(--color-interactive-disabled);
        border-radius: 4px;
    }
    
    .claims-list::-webkit-scrollbar-thumb,
    .limitations-list::-webkit-scrollbar-thumb,
    .results-list::-webkit-scrollbar-thumb {
        background: var(--color-text-secondary);
        border-radius: 4px;
        opacity: 1.0; /* Fully opaque */
    }
    
    .claims-list::-webkit-scrollbar-thumb:hover,
    .limitations-list::-webkit-scrollbar-thumb:hover,
    .results-list::-webkit-scrollbar-thumb:hover {
        background: var(--color-text-primary);
        opacity: 1.0; /* Fully opaque on hover */
    }

    .modal-content {
        padding: var(--spacing-6);
    }
    
    .instructions {
        padding: var(--spacing-4);
        margin-bottom: var(--spacing-6);
        font-size: var(--font-size-sm);
        background-color: var(--color-interactive-disabled);
        border-radius: var(--border-radius-xl);
        border-left: var(--border-width-3) solid var(--color-claims-modal-action-primary);
        box-shadow: var(--shadow-sm);
    }
    
    .instructions p {
        margin: 0 0 var(--spacing-2) 0;
        line-height: var(--line-height-normal);
        color: var(--color-text-secondary);
    }
    
    .assignment-container {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-6);
        margin-bottom: var(--spacing-6);
    }

    .panel {
        display: flex;
        flex-direction: column;
        width: 100%;
        overflow: hidden;
        background-color: var(--color-claims-modal-panel-secondary);
        border: 1px solid var(--border-color-light);
        border-radius: var(--border-radius-xl);
        box-shadow: var(--shadow-sm);
        transition: var(--transition-all);
    }
    
    .claims-panel {
        min-height: 300px;
        max-height: 500px;
    }
    
    .limitations-panel {
        min-height: 200px;
        max-height: 400px;
    }
    
    .panel h3 {
        padding: var(--spacing-4) var(--spacing-5);
        margin: 0;
        font-size: 1.1rem;
        font-weight: var(--font-weight-semibold);
        color: var(--color-text-primary);
        background-color: var(--color-claims-modal-panel-tertiary);
        border-bottom: var(--border-light);
    }
    
    .limitations-input {
        display: flex;
        align-items: center;
        padding: var(--spacing-4);
        background-color: var(--color-claims-modal-panel-primary);
        border-bottom: var(--border-light);
    }

    .claims-info {
        padding: var(--spacing-3) var(--spacing-4);
        font-size: 13px;
        line-height: var(--line-height-normal);
        color: var(--color-text-secondary);
        background-color: var(--color-claims-modal-panel-primary);
        border-bottom: var(--border-light);
    }

    .claim-selection-controls {
        display: flex;
        flex-wrap: wrap;
        margin-top: var(--spacing-2);
        gap: var(--spacing-2);
    }

    /* Custom button sizes for this modal */
    .claim-selection-controls .modal-button {
        padding: 0.3rem 0.6rem;
        font-size: var(--font-size-xs);
        white-space: nowrap;
    }
    
    .limitations-input input {
        flex: 1;
        padding: var(--spacing-3) var(--spacing-4);
        font-size: var(--font-size-sm);
        color: var(--color-text-primary);
        background-color: var(--color-input-bg);
        border: 1px solid var(--border-color-default);
        border-radius: var(--border-radius-lg);
        box-shadow: none;
        transition: var(--transition-all);
    }
    
    .limitations-input input:focus {
        background-color: var(--color-input-bg-focus);
        border-color: var(--color-claims-modal-action-primary);
        outline: none;
        box-shadow: var(--shadow-focus-spec);
    }
    
    .limitations-input .modal-button {
        padding: var(--spacing-2) var(--spacing-3);
        margin-left: var(--spacing-2);
        font-size: var(--font-size-sm);
    }
    
    .limitations-list {
        max-height: calc(400px - 80px);
        padding: var(--spacing-4);
        overflow-y: auto;
    }
    
    .claims-list {
        flex: 1;
        max-height: calc(500px - 120px);
        padding: var(--spacing-4);
        overflow-y: auto;
    }
    
    .empty-message {
        padding: var(--spacing-4);
        margin-top: var(--spacing-6);
        text-align: center;
        color: var(--color-text-tertiary);
        background-color: var(--color-interactive-disabled);
        border-radius: var(--border-radius-lg);
        font-style: italic;
    }
    
    .limitation-item, .claim-item {
        display: flex;
        align-items: flex-start;
        padding: var(--spacing-3);
        margin-bottom: var(--spacing-3);
        background-color: var(--color-interactive-default);
        border: 1px solid var(--border-color-light);
        border-radius: var(--border-radius-lg);
        border-left: var(--border-width-3) solid var(--color-claims-modal-action-primary);
        box-shadow: none;
        transition: var(--transition-all);
    }

    .limitation-item:hover, .claim-item:hover {
        background-color: var(--color-interactive-hover);
        box-shadow: var(--shadow-sm);
        transform: translateY(-1px);
    }

    .claim-item.deselected {
        background-color: var(--color-interactive-disabled);
        opacity: var(--opacity-deselected);
        border-left-color: var(--border-color-light);
    }

    .claim-checkbox {
        display: flex;
        align-items: center;
        padding: 3px;
        margin-right: var(--spacing-2);
        background-color: var(--color-input-bg);
        border-radius: var(--border-radius-sm);
        transition: var(--transition-colors);
    }

    .claim-checkbox:hover {
        background-color: var(--color-interactive-hover);
    }

    .claim-checkbox input {
        width: 20px;
        height: 20px;
        cursor: pointer;
        accent-color: var(--color-claims-modal-checkbox-accent);
    }

    .limitation-number, .claim-number {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 26px;
        min-width: 26px;
        height: 26px;
        margin-right: 0.75rem;
        font-size: 12px;
        font-weight: bold;
        color: var(--color-claims-modal-score-text);
        background-color: var(--color-claims-modal-action-primary);
        border-radius: 50%;
        box-shadow: none;
    }

    .deselected .claim-number {
        background-color: var(--color-claims-modal-deselected-number);
    }
    
    .limitation-text, .claim-text {
        flex: 1;
        font-size: 14px;
        line-height: 1.5;
        color: var(--color-text-primary);
        cursor: default; /* Show default cursor to indicate text isn't clickable */
    }
    
    .whitespace-preserve {
        white-space: pre-wrap;
    }
    
    /* All claims now use the same border color */
    
    .limitation-item .modal-button-danger {
        display: flex;
        justify-content: center;
        align-items: center;
        width: 28px;
        height: 28px;
        padding: 0;
        margin-left: 0.5rem;
        font-size: 18px;
        border-radius: 50%;
    }
    
    .action-bar {
        display: flex;
        flex-direction: column;
        align-items: center;
        padding: 1rem;
        margin: 1.5rem 0;
        background-color: var(--color-panel-tertiary-bg);
        border-radius: var(--border-radius, 8px);
    }
    
    .action-bar .modal-button {
        padding: 0.75rem 2rem;
        font-size: 1rem;
        font-weight: 600;
        border-radius: var(--border-radius-pill, 16px);
    }
    
    .error-message {
        padding: 0.5rem 1rem;
        margin-top: 0.75rem;
        font-size: 14px;
        color: var(--color-error);
        background-color: var(--color-error-bg);
        border-radius: var(--border-radius, 8px);
        border-left: 3px solid var(--color-error);
    }
    
    .results-container {
        padding: 1.5rem;
        padding-top: 1.5rem;
        margin-top: 2rem;
        background-color: var(--color-panel-secondary-bg);
        border: 1px solid var(--border-color-default);
        border-radius: var(--border-radius, 8px);
    }
    
    .results-container h3 {
        margin: 0 0 1rem;
        font-size: 1.2rem;
        font-weight: 600;
        color: var(--color-text-primary);
    }
    
    .results-list {
        display: flex;
        flex-direction: column;
        gap: 1rem;
    }
    
    .limitation-group {
        margin-bottom: 1.5rem;
        overflow: hidden;
        background-color: var(--color-panel-primary-bg);
        border: 1px solid var(--border-color-light);
        border-radius: 12px;
        box-shadow: var(--shadow-sm);
    }
    
    /* Dynamic limitation group colors */
    .limitation-group:nth-child(1) .limitation-header {
        background-color: var(--color-claims-modal-limitation-bg-1);
        border-left: 4px solid var(--color-claims-modal-limitation-border-1);
    }

    .limitation-group:nth-child(2) .limitation-header {
        background-color: var(--color-claims-modal-limitation-bg-2);
        border-left: 4px solid var(--color-claims-modal-limitation-border-2);
    }

    .limitation-group:nth-child(3) .limitation-header {
        background-color: var(--color-claims-modal-limitation-bg-3);
        border-left: 4px solid var(--color-claims-modal-limitation-border-3);
    }

    .limitation-group:nth-child(4) .limitation-header {
        background-color: var(--color-claims-modal-limitation-bg-4);
        border-left: 4px solid var(--color-claims-modal-limitation-border-4);
    }

    .limitation-group:nth-child(5) .limitation-header {
        background-color: var(--color-claims-modal-limitation-bg-5);
        border-left: 4px solid var(--color-claims-modal-limitation-border-5);
    }
    
    .limitation-header {
        padding: 0.875rem 1rem;
        padding-left: 1.25rem;
        background-color: var(--color-panel-tertiary-bg);
        border-bottom: 1px solid var(--border-color-light);
        transition: background-color 0.2s ease;
    }
    
    .result-item {
        overflow: hidden;
        border-bottom: 1px solid var(--border-color-light);
    }
    
    .result-item:last-child {
        border-bottom: none;
    }
    
    .limitation-label {
        margin-right: 0.5rem;
        font-weight: 600;
        color: var(--color-text-primary);
    }
    
    .result-claim {
        display: flex;
        align-items: center;
        padding: 0.875rem 1rem;
        transition: background-color 0.2s ease;
    }
    
    .result-claim:hover {
        background-color: var(--color-interactive-hover);
    }
    
    .claim-label {
        display: inline-block;
        min-width: 80px;
        margin-right: var(--spacing-2);
        font-weight: var(--font-weight-semibold);
        color: var(--color-text-primary);
    }
    
    .claim-content {
        flex: 1;
        margin-right: var(--spacing-3);
        font-size: var(--font-size-sm);
        line-height: var(--line-height-normal);
        color: var(--color-text-primary);
    }
    
    .match-score {
        padding: var(--spacing-1) var(--spacing-2);
        font-size: var(--font-size-xs);
        font-weight: var(--font-weight-semibold);
        color: var(--color-claims-modal-score-text);
        background-color: var(--color-claims-modal-score-bg);
        border-radius: var(--border-radius-pill);
        box-shadow: none;
    }
    
    .no-claims-message {
        padding: var(--spacing-4) var(--spacing-5);
        margin: var(--spacing-2) var(--spacing-3);
        font-size: var(--font-size-sm);
        text-align: center;
        color: var(--color-text-secondary);
        background-color: var(--color-interactive-disabled);
        border-radius: var(--border-radius-lg);
        font-style: italic;
    }
    
    .apply-action {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
        padding: var(--spacing-4);
        margin-top: var(--spacing-6);
        background-color: var(--color-panel-tertiary-bg);
        border: 1px solid var(--border-color-light);
        border-radius: var(--border-radius-lg);
    }
    
    .apply-section .modal-button {
        padding: var(--spacing-3) var(--spacing-8);
        font-size: var(--font-size-base);
        font-weight: var(--font-weight-semibold);
        border-radius: var(--border-radius-pill);
    }
    
    .progress-wrapper {
        display: flex;
        flex-direction: column;
        align-items: center;
        width: 100%;
        max-width: 400px;
        margin-top: var(--spacing-4);
    }
    
    .progress-container {
        position: relative;
        width: 100%;
        height: 20px;
        overflow: hidden;
        background-color: var(--color-interactive-disabled);
        border-radius: var(--border-radius-pill);
        box-shadow: inset 0 1px 2px var(--border-color-default);
    }
    
    .progress-fill {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background-color: var(--color-claims-modal-progress-fill);
        background-image: linear-gradient(
            45deg,
            var(--color-claims-modal-progress-stripe) 25%,
            transparent 25%,
            transparent 50%,
            var(--color-claims-modal-progress-stripe) 50%,
            var(--color-claims-modal-progress-stripe) 75%,
            transparent 75%,
            transparent
        );
        background-size: 20px 20px;
        transition: transform 1.5s ease-out;
        transform: scaleX(0);
        animation: progress-stripes 1s linear infinite;
    }
    
    @keyframes progress-stripes {
        0% { background-position: 0 0; }
        100% { background-position: 20px 0; }
    }
    
    
    .progress-text {
        margin-top: var(--spacing-2);
        font-size: var(--font-size-sm);
        font-weight: var(--font-weight-semibold);
        text-align: center;
        color: var(--color-text-primary);
    }
    
    .time-estimate {
        margin-top: var(--spacing-1);
        font-size: var(--font-size-xs);
        font-weight: var(--font-weight-normal);
        color: var(--color-text-secondary);
    }
</style>